import * as Sentry from "@sentry/react";

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error ?? "Unknown service worker registration error");
}

/**
 * Recover when vite-plugin-pwa/Workbox cannot register the generated worker.
 *
 * Some browsers reject Workbox with an opaque `TypeError: Internal error` even though registering
 * the same `/sw.js` through the native API succeeds. The first rejection is therefore diagnostic,
 * not an application error. Only report an exception when the native recovery also fails.
 */
export async function recoverServiceWorkerRegistration(workboxError: unknown): Promise<boolean> {
  if (!("serviceWorker" in navigator)) {
    Sentry.captureException(workboxError, {
      tags: { sw_registration_failed: "unsupported_after_workbox" },
      extra: { workboxError: errorMessage(workboxError), hadExistingController: false },
    });
    return false;
  }

  const hadExistingController = Boolean(navigator.serviceWorker.controller);

  try {
    await navigator.serviceWorker.register("/sw.js", { scope: "/", type: "classic" });
    Sentry.addBreadcrumb({
      category: "pwa",
      level: "warning",
      message: "Service worker recovered through native registration",
      data: {
        workboxError: errorMessage(workboxError),
        hadExistingController,
      },
    });
    return true;
  } catch (nativeError) {
    console.warn("Native service worker fallback failed", nativeError);
    Sentry.captureException(nativeError, {
      tags: { sw_registration_failed: "native_fallback" },
      extra: {
        workboxError: errorMessage(workboxError),
        hadExistingController,
      },
    });
    return false;
  }
}
